import * as React from 'react';
import { styled } from '@mui/material/styles';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import GreenPrimaryButton from '../../components/shared/buttons/GreenPrimaryButton';
import MainSubsectionTitle from '../../components/shared/typography/MainSubsectionTitle';
import CopyTextField from '../../components/shared/inputs/CopyTextField';
import { Box } from '@mui/material';
import CancelButton from '../../components/shared/buttons/CancelButton';
import HelpTooltipIcon from '../../components/shared/others/HelpTooltipIcon';
import TypeFormat from './TypeFormat';
import MainTooltip from '../../components/shared/others/MainTooltip';
import ErrorBox from '../../components/shared/boxes/ErrorBox';
import { createOrUpdateIdpConfig } from '../api/idp_config';

const SecurityModal = styled(Dialog)(({ theme }) => ({
  '& .MuiDialogContent-root': {
    padding: theme.spacing(7),
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    Width: '38rem',
  },
  '& .MuiDialogActions-root': {
    padding: theme.spacing(1),
  },
}));

export default function SsoModal({ idpConfig, updateIdpConfigCallback }) {
  const [open, setOpen] = React.useState(false);
  const [selectedFormat, setSelectedFormat] = React.useState('URL');
  const [configApiError, setConfigApiError] = React.useState({});
  const [configButtonApiError, setConfigButtonApiError] = React.useState({});

  const updateSelectedFormat = (v) => setSelectedFormat(v);
  const successCallback = (responseJson) => updateIdpConfigCallback(responseJson);
  const errorCallback = (errorMessage) => setConfigApiError({ errorMessage: errorMessage });
  const configButtonErrorCallback = (errorMessage) => setConfigButtonApiError({ errorMessage: errorMessage });

  const handleClickOpen = async () => {
    const idpConfigNotPresent = idpConfig === null || Object.keys(idpConfig).length === 0;

    if (idpConfigNotPresent) {
      const updateMetadata = {
        metadata_url: '',
        metadata_xml: '',
      };

      createOrUpdateIdpConfig({
        idpConfig: updateMetadata,
        successCallback: (responseJson) => {
          updateIdpConfigCallback(responseJson);
          setOpen(true);
        },
        errorCallback: configButtonErrorCallback,
      });
    } else {
      setOpen(true);
    }
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleSubmit = () => {
    const buildBody = (format) => {
      const idpConfigBody = {};
      if (format === 'URL') {
        idpConfigBody['metadata_url'] = idpConfig?.metadataUrl;
      } else {
        idpConfigBody['metadata_xml'] = idpConfig?.metadataXml;
      }

      return {
        account_idp_config: idpConfigBody,
      };
    };

    createOrUpdateIdpConfig({
      idpConfig: buildBody(selectedFormat),
      successCallback: (responseJson) => {
        updateIdpConfigCallback(responseJson);
        setOpen(false);
        setConfigApiError({});
      },
      errorCallback: errorCallback,
    });
  };

  return (
    <React.Fragment>
      {configButtonApiError.errorMessage && (
        <ErrorBox errorMessage={configButtonApiError.errorMessage} onClose={() => setConfigButtonApiError({})} />
      )}
      <GreenPrimaryButton variant="contained" onClick={handleClickOpen}>
        Configurar
      </GreenPrimaryButton>
      <SecurityModal
        onClose={handleClose}
        aria-labelledby="customized-dialog-title"
        open={open}
        sx={{ display: 'flex', justifyContent: 'center', alignContent: 'center' }}
      >
        <DialogTitle sx={{ m: '0 auto', p: 2 }} id="customized-dialog-title">
          Dados para autenticação
        </DialogTitle>
        <IconButton
          aria-label="close"
          onClick={handleClose}
          sx={(theme) => ({
            position: 'absolute',
            right: 8,
            top: 8,
            color: theme.palette.grey[500],
          })}
        >
          <CloseIcon />
        </IconButton>
        <DialogContent dividers>
          {configApiError.errorMessage && (
            <ErrorBox errorMessage={configApiError.errorMessage} onClose={() => setConfigApiError({})} />
          )}
          <Box display="flex" flexDirection="row" gap="10px" alignItems={'center'}>
            <MainSubsectionTitle>Metadados</MainSubsectionTitle>
            <MainTooltip title="Informe a URL onde os metadados SAML do provedor de identidade (IdP) estão disponíveis ou cole diretamente o conteúdo XML dos metadados. Esses dados são utilizados para configurar automaticamente as informações de autenticação, como endpoints e certificados.">
              <HelpTooltipIcon />
            </MainTooltip>
          </Box>
          <TypeFormat
            idpConfig={idpConfig}
            updateIdpConfigCallback={updateIdpConfigCallback}
            selectedFormat={selectedFormat}
            setSelectedFormat={updateSelectedFormat}
          />
          <Box>
            <Box display="flex" flexDirection="row" gap="10px">
              <MainSubsectionTitle>Credenciais</MainSubsectionTitle>
              <MainTooltip title="Copie e cole as credenciais abaixo no IdP para configuração da sua aplicação SSO SAML.">
                <HelpTooltipIcon />
              </MainTooltip>
            </Box>
            <CopyTextField
              label="Service Provider (Produttivo) Entity ID"
              value={idpConfig?.spEntityId}
            ></CopyTextField>
            <CopyTextField
              label="Service Provider (Produttivo) Assertion Consumer Service (ACS) URL"
              value={idpConfig?.acsUrl}
            ></CopyTextField>
            <CopyTextField
              label="Service Provider (Produttivo) Single Log Out (SLO) URL:"
              value={idpConfig?.sloUrl}
            ></CopyTextField>
          </Box>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'space-between' }}>
          <CancelButton onClick={handleClose}>Cancelar</CancelButton>
          <GreenPrimaryButton autoFocus onClick={handleSubmit}>
            Salvar
          </GreenPrimaryButton>
        </DialogActions>
      </SecurityModal>
    </React.Fragment>
  );
}
